import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, DollarSign, Building2, Users, RefreshCw,
  Loader2, ArrowUp, ArrowDown, Calendar, Target, Crown, PieChart as PieChartIcon,
  BarChart3, Percent, Bell, Mail, UserPlus, CreditCard, AlertTriangle
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend, AreaChart, Area
} from "recharts";

const COLORS = ["#059669", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];

const PLAN_LABELS = {
  starter: "Starter",
  pro: "Pro",
  elite: "Elite",
  trial: "Trial",
};

function KpiCard({ icon: Icon, label, value, sub, prefix = "", suffix = "", growth, inverse = false, color = "emerald" }) {
  const isPositive = inverse ? growth < 0 : growth > 0;
  const colorClasses = {
    emerald: "bg-emerald-50 text-emerald-600",
    blue: "bg-blue-50 text-blue-600",
    amber: "bg-amber-50 text-amber-600",
    red: "bg-red-50 text-red-600",
    purple: "bg-purple-50 text-purple-600",
  };
  
  return (
    <Card className="hover:shadow-md transition-shadow" data-testid={`investor-kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className={`w-10 h-10 rounded-xl ${colorClasses[color]} flex items-center justify-center`}>
            <Icon className="w-5 h-5" />
          </div>
          {growth !== undefined && growth !== 0 && (
            <Badge className={`text-[10px] ${isPositive ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"} border-0 gap-0.5`}>
              {isPositive ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
              {Math.abs(growth).toFixed(1)}%
            </Badge>
          )}
        </div>
        <p className="text-2xl font-black text-foreground tracking-tight">
          {prefix}{typeof value === "number" ? value.toLocaleString("pt-BR") : value}{suffix}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
        {sub && <p className="text-[10px] text-muted-foreground/70 mt-0.5">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function RecentRestaurantCard({ restaurant }) {
  const statusColors = {
    active: "bg-emerald-100 text-emerald-700",
    trial: "bg-amber-100 text-amber-700",
    suspended: "bg-red-100 text-red-700",
  };
  
  return (
    <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
      <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center text-emerald-700 font-bold text-sm flex-shrink-0">
        {restaurant.name?.charAt(0)?.toUpperCase() || "R"}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-sm truncate">{restaurant.name}</p>
        <p className="text-xs text-muted-foreground truncate">{restaurant.email}</p>
      </div>
      <div className="text-right flex-shrink-0">
        <Badge className={`text-[9px] ${statusColors[restaurant.subscription_status] || statusColors.trial}`}>
          {restaurant.subscription_status === "active" ? "Ativo" : 
           restaurant.subscription_status === "suspended" ? "Suspenso" : "Trial"}
        </Badge>
        <p className="text-[10px] text-muted-foreground mt-1">
          {restaurant.created_at ? new Date(restaurant.created_at).toLocaleDateString("pt-BR") : ""}
        </p>
      </div>
    </div>
  );
}

export function InvestorDashboardTab({ adminAxios }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAxios.get("/platform-metrics/dashboard");
      setData(res.data);
    } catch (err) {
      toast.error("Erro ao carregar metricas de investidores");
      console.error(err);
    }
    setLoading(false);
  }, [adminAxios]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading && !data) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-20 text-muted-foreground">
        <AlertTriangle className="w-12 h-12 mx-auto mb-3 opacity-30" />
        <p>Erro ao carregar dados. Tente novamente.</p>
        <Button onClick={fetchData} className="mt-4" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" /> Tentar novamente
        </Button>
      </div>
    );
  }

  const { summary, growth, processed_sales, recent_restaurants, daily_stats } = data;

  // Prepare chart data
  const mrrBreakdownData = summary.mrr_breakdown?.map(item => ({
    name: PLAN_LABELS[item.plan] || item.plan,
    value: item.revenue,
    count: item.count,
  })) || [];

  const dailyChartData = daily_stats?.map(d => ({
    date: d._id,
    pedidos: d.orders,
    receita: d.revenue,
  })) || [];

  return (
    <div className="space-y-6" data-testid="investor-dashboard-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 shadow-lg shadow-amber-500/20">
            <Crown className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="font-black text-xl tracking-tight">Dashboard de Investidores</h2>
            <p className="text-xs text-muted-foreground">Metricas de negocio da plataforma Kaiso System</p>
          </div>
        </div>
        <Button size="sm" variant="outline" className="gap-1.5 h-8" onClick={fetchData} data-testid="refresh-investor-data">
          <RefreshCw className="w-3.5 h-3.5" /> Atualizar
        </Button>
      </div>

      {/* Key Financial Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          icon={DollarSign}
          label="MRR"
          value={summary.mrr}
          suffix=" EUR"
          sub="Receita Mensal Recorrente"
          color="emerald"
        />
        <KpiCard
          icon={Target}
          label="ARR"
          value={summary.arr}
          suffix=" EUR"
          sub="Receita Anual Recorrente"
          color="blue"
        />
        <KpiCard
          icon={Building2}
          label="Restaurantes Pagos"
          value={summary.paid_restaurants}
          sub={`${summary.trial_restaurants} em trial`}
          color="purple"
        />
        <KpiCard
          icon={Percent}
          label="Taxa de Churn"
          value={summary.churn_rate}
          suffix="%"
          sub="Ultimos 30 dias"
          color={summary.churn_rate > 5 ? "red" : "emerald"}
          inverse
        />
      </div>

      {/* Growth Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          icon={UserPlus}
          label="Novos Hoje"
          value={growth.new_restaurants_today}
          sub="Restaurantes cadastrados hoje"
          color="emerald"
        />
        <KpiCard
          icon={Calendar}
          label="Novos no Mes"
          value={growth.new_restaurants_month}
          sub="Restaurantes este mes"
          color="blue"
        />
        <KpiCard
          icon={TrendingUp}
          label="Crescimento Receita"
          value={growth.revenue_growth_pct}
          suffix="%"
          sub="vs. mes anterior"
          growth={growth.revenue_growth_pct}
          color="emerald"
        />
        <KpiCard
          icon={BarChart3}
          label="Crescimento Pedidos"
          value={growth.orders_growth_pct}
          suffix="%"
          sub="vs. mes anterior"
          growth={growth.orders_growth_pct}
          color="blue"
        />
      </div>

      {/* Platform Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiCard
          icon={Building2}
          label="Total Restaurantes"
          value={summary.total_restaurants}
          color="purple"
        />
        <KpiCard
          icon={Users}
          label="Total Usuarios"
          value={summary.total_users}
          color="blue"
        />
        <KpiCard
          icon={Building2}
          label="Ativos"
          value={summary.active_restaurants}
          color="emerald"
        />
        <KpiCard
          icon={Building2}
          label="Trial"
          value={summary.trial_restaurants}
          color="amber"
        />
        <KpiCard
          icon={Building2}
          label="Suspensos"
          value={summary.suspended_restaurants}
          color="red"
        />
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-2 gap-4">
        {/* MRR Breakdown */}
        <Card data-testid="chart-mrr-breakdown">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <PieChartIcon className="w-4 h-4 text-emerald-600" />
              Composicao do MRR por Plano
            </CardTitle>
          </CardHeader>
          <CardContent>
            {mrrBreakdownData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={mrrBreakdownData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ name, percent, count }) => `${name}: ${count} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={false}
                  >
                    {mrrBreakdownData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => [`${v.toFixed(2)} EUR`]} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">Sem dados de MRR</p>
            )}
          </CardContent>
        </Card>

        {/* Daily Orders & Revenue */}
        <Card data-testid="chart-daily-stats">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-blue-600" />
              Pedidos e Receita (30 dias)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dailyChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={dailyChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={v => v?.slice(8) || ""} />
                  <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} />
                  <Tooltip
                    formatter={(v, name) => [
                      name === "receita" ? `${v.toFixed(2)} EUR` : v,
                      name === "receita" ? "Receita" : "Pedidos"
                    ]}
                    labelFormatter={l => `Data: ${l}`}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Area yAxisId="left" type="monotone" dataKey="pedidos" name="Pedidos" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} />
                  <Area yAxisId="right" type="monotone" dataKey="receita" name="Receita" stroke="#059669" fill="#059669" fillOpacity={0.3} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">Sem dados de pedidos</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Processed Sales */}
      <Card data-testid="processed-sales-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-purple-600" />
            Volume de Vendas Processado
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-emerald-50 rounded-lg">
              <p className="text-2xl font-black text-emerald-700">{processed_sales.today_orders}</p>
              <p className="text-xs text-emerald-600">Pedidos Hoje</p>
              <p className="text-sm font-bold text-emerald-800 mt-1">{processed_sales.today_revenue?.toFixed(2)} EUR</p>
            </div>
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <p className="text-2xl font-black text-blue-700">{processed_sales.month_orders?.toLocaleString()}</p>
              <p className="text-xs text-blue-600">Pedidos Este Mes</p>
              <p className="text-sm font-bold text-blue-800 mt-1">{processed_sales.month_revenue?.toFixed(2)} EUR</p>
            </div>
            <div className="text-center p-3 bg-amber-50 rounded-lg">
              <p className="text-2xl font-black text-amber-700">{processed_sales.last_month_revenue?.toFixed(2)}</p>
              <p className="text-xs text-amber-600">Receita Mes Passado</p>
              <p className="text-sm font-bold text-amber-800 mt-1">EUR</p>
            </div>
            <div className="text-center p-3 bg-purple-50 rounded-lg">
              <p className="text-2xl font-black text-purple-700">
                {processed_sales.month_revenue > 0 && processed_sales.last_month_revenue > 0
                  ? ((processed_sales.month_revenue - processed_sales.last_month_revenue) / processed_sales.last_month_revenue * 100).toFixed(1)
                  : "0"}%
              </p>
              <p className="text-xs text-purple-600">Crescimento MoM</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Restaurants */}
      <Card data-testid="recent-restaurants-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Bell className="w-4 h-4 text-amber-600" />
            Ultimos Restaurantes Cadastrados
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {recent_restaurants?.length > 0 ? (
              recent_restaurants.map((r, i) => (
                <RecentRestaurantCard key={i} restaurant={r} />
              ))
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">Nenhum restaurante recente</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="text-center">
        <p className="text-[10px] text-muted-foreground">
          Dados gerados em: {data.generated_at ? new Date(data.generated_at).toLocaleString("pt-BR") : "-"}
        </p>
      </div>
    </div>
  );
}
